import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../app/hooks';
import { motion } from 'framer-motion';
import { RootState } from '../app/store';
import { fetchExpenses } from '../app/slices/expenseSlice';
import { fetchSessions } from '../app/slices/sessionSlice';
import DashboardCard from '../components/DashboardCard';
import LoadingSpinner from '../components/LoadingSpinner';
import DashboardCTAs from '../ui/dashboard/DashboardCTAs';
import { Navigate } from 'react-router-dom';
import {
  CurrencyDollarIcon,
  ReceiptRefundIcon,
  DocumentTextIcon,
  CalendarIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { taxUtils, TaxEngine } from '../lib/taxEngine';
import { trackUsage, startDwellTimer } from '../lib/telemetry';
import { Link } from 'react-router-dom';

const Dashboard: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    user,
    isLoading: authLoading,
    initialized,
  } = useSelector((state: RootState) => state.auth);
  const {
    expenses,
    totalExpenses,
    totalITCs,
    isLoading: expensesLoading,
  } = useSelector((state: RootState) => state.expenses);
  const {
    sessions,
    totalEarnings,
    isLoading: sessionsLoading,
  } = useSelector((state: RootState) => state.sessions);

  useEffect(() => {
    if (user?.uid) {
      dispatch(fetchExpenses(user.uid));
      dispatch(fetchSessions(user.uid));
    }
  }, [dispatch, user?.uid]);

  useEffect(() => {
    // Track page view and dwell time
    trackUsage('usage_page_view', { page: 'Dashboard' }).catch(console.warn);
    const stop = startDwellTimer('Dashboard');
    return stop;
  }, []);

  // Calculate current month stats using config-driven tax engine
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();
  const provinceCode = user?.province || 'ON';
  const taxEngine = new TaxEngine(provinceCode);

  const thisMonthExpenses = expenses.filter(expense => {
    const expenseDate = new Date(expense.date);
    return expenseDate.getMonth() === currentMonth && expenseDate.getFullYear() === currentYear;
  });

  const thisMonthSessions = sessions.filter(session => {
    const sessionDate = new Date(session.startTime.toDate());
    return sessionDate.getMonth() === currentMonth && sessionDate.getFullYear() === currentYear;
  });

  const thisMonthEarnings = thisMonthSessions.reduce(
    (sum, session) => sum + session.grossEarnings,
    0
  );
  // Calculate deductibles for future use
  // const thisMonthDeductibles = thisMonthExpenses.reduce(
  //   (sum, expense) => sum + taxEngine.calculateDeductibleAmount(expense.amount, expense.businessUsePct),
  //   0
  // );
  const thisMonthGSTCollected = thisMonthSessions.reduce(
    (sum, session) => sum + session.gstCollected,
    0
  );
  const thisMonthITCs = thisMonthExpenses.reduce(
    (sum, expense) => sum + taxEngine.calculateClaimableITC(expense),
    0
  );
  const thisMonthGSTToRemit = thisMonthGSTCollected - thisMonthITCs;

  // Calculate next filing date (quarterly)
  const getNextFilingDate = () => {
    const now = new Date();
    const currentQuarter = Math.floor(now.getMonth() / 3);
    const nextQuarter = (currentQuarter + 1) % 4;
    const nextYear = nextQuarter === 0 ? now.getFullYear() + 1 : now.getFullYear();
    const filingMonth = nextQuarter * 3 + 1; // January, April, July, October
    return new Date(nextYear, filingMonth, 31); // Last day of the month after quarter end
  };

  const nextFilingDate = getNextFilingDate();
  const daysUntilFiling = Math.ceil(
    (nextFilingDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
  );

  // Redirect to onboarding if profile incomplete
  if (user && (!user.province || !user.distanceUnit)) {
    return <Navigate to="/onboarding" replace />;
  }

  // Show loading spinner if auth is not initialized or user data is loading
  if (!initialized || authLoading || !user || expensesLoading || sessionsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" text="Loading dashboard..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* CRITICAL: Province Not Set Warning */}
      {(!user?.province || user.province === '') && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-50 border-2 border-red-400 rounded-xl p-5 shadow-lg"
        >
          <div className="flex items-start space-x-3">
            <ExclamationTriangleIcon className="h-8 w-8 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-lg font-bold text-red-900 mb-2">
                ⚠️ Action Required: Set Your Province
              </h3>
              <p className="text-sm text-red-800 mb-3">
                <strong>You cannot add expenses until you set your province.</strong> Tax rates
                (GST/HST/PST) vary significantly by province:
              </p>
              <ul className="text-sm text-red-700 mb-4 space-y-1 list-disc list-inside">
                <li>
                  <strong>Ontario:</strong> 13% HST
                </li>
                <li>
                  <strong>BC:</strong> 5% GST + 7% PST = 12%
                </li>
                <li>
                  <strong>Alberta:</strong> 5% GST only
                </li>
                <li>
                  <strong>Quebec:</strong> 5% GST + 9.975% QST = 14.975%
                </li>
              </ul>
              <Link
                to="/profile"
                className="inline-flex items-center px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors shadow-md"
              >
                Go to Profile Settings →
              </Link>
            </div>
          </div>
        </motion.div>
      )}

      {/* Branding Banner */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 border-2 border-blue-200 rounded-xl p-5 shadow-md"
      >
        <div className="flex items-center justify-center space-x-3 mb-2">
          <div className="h-10 w-10 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl flex items-center justify-center shadow-lg">
            <span className="text-white font-bold text-lg">A</span>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-700 bg-clip-text text-transparent">
            AutoTaxPilot™
          </h1>
        </div>
        <p className="text-center text-sm text-gray-600 font-medium">
          Your Trusted Partner for Uber Tax Compliance • On Your Side • Audit-Ready
        </p>
      </motion.div>

      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <motion.h2
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="text-3xl font-bold leading-7 text-gray-900 sm:text-4xl sm:truncate"
          >
            Welcome back, {user?.displayName?.split(' ')[0] || 'Driver'}! 👋
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="mt-2 text-base text-gray-600"
          >
            Here's your business overview for{' '}
            {new Date().toLocaleDateString('en-CA', { month: 'long', year: 'numeric' })}
          </motion.p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          <DashboardCTAs />
        </div>
      </div>

      {/* Quick Stats Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <DashboardCard
          title="Earnings This Month"
          value={thisMonthEarnings}
          subtitle={`${thisMonthSessions.length} sessions`}
          icon={CurrencyDollarIcon}
          color="success"
        />

        <DashboardCard
          title={`${taxEngine.getTaxSchemeName()} Collected`}
          value={thisMonthGSTCollected}
          subtitle={`${taxEngine.getTaxRatePercent().toFixed(1)}% rate`}
          icon={DocumentTextIcon}
          color="primary"
        />

        <DashboardCard
          title="ITC Claimable"
          value={thisMonthITCs}
          subtitle={`${thisMonthExpenses.length} expenses`}
          icon={ReceiptRefundIcon}
          color="success"
        />

        <DashboardCard
          title="Net Owing"
          value={thisMonthGSTToRemit}
          subtitle={thisMonthGSTToRemit > 0 ? 'Amount to remit' : 'Refund expected'}
          icon={DocumentTextIcon}
          color={thisMonthGSTToRemit > 0 ? 'warning' : 'success'}
        />

        <DashboardCard
          title="Next CRA Filing"
          value={`${daysUntilFiling} days`}
          subtitle={nextFilingDate.toLocaleDateString('en-CA')}
          icon={CalendarIcon}
          color={daysUntilFiling < 30 ? 'danger' : 'gray'}
        />
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 gap-5">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card"
        >
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">Recent Activity</h3>
          </div>
          <div className="space-y-4">
            {/* Recent expenses */}
            {thisMonthExpenses.slice(0, 3).map((expense, _index) => (
              <div key={expense.id} className="flex items-center space-x-4">
                <div className="flex-shrink-0">
                  <div className="h-8 w-8 bg-primary-100 rounded-full flex items-center justify-center">
                    <ReceiptRefundIcon className="h-4 w-4 text-primary-600" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {expense.subCategory} - {expense.category}
                  </p>
                  <p className="text-sm text-gray-500">
                    {new Date(expense.date).toLocaleDateString('en-CA')}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900">
                    {taxUtils.formatCurrency(expense.amount)}
                  </p>
                  <p className="text-xs text-gray-500">
                    ITC: {taxUtils.formatCurrency(expense.claimableITC)}
                  </p>
                </div>
              </div>
            ))}

            {thisMonthExpenses.length === 0 && (
              <div className="text-center py-8">
                <ReceiptRefundIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No expenses yet</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Start tracking your business expenses to maximize deductions.
                </p>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Summary Stats */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="card"
      >
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">Year-to-Date Summary</h3>
        </div>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">
              {taxUtils.formatCurrency(totalEarnings)}
            </p>
            <p className="text-sm text-gray-500">Total Earnings</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">
              {taxUtils.formatCurrency(totalExpenses)}
            </p>
            <p className="text-sm text-gray-500">Total Expenses</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">{taxUtils.formatCurrency(totalITCs)}</p>
            <p className="text-sm text-gray-500">Total ITCs</p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Dashboard;
